import React, { useCallback, useState } from 'react';
import { useHistory } from "react-router-dom";

import Api from 'src/services/api';

import HeaderExternalSkeleton from './header-external-skeleton';

import { Grid, Button, Toolbar, Box, Typography, Divider, darken, Skeleton } from '@mui/material';
import { styled } from '@mui/styles';

const LogoImage = styled("img")(({ isLoaded }) => ({
    maxWidth: 160,
    maxHeight: 44,
    objectFit: 'contain',
    objectPosition: "center",
    opacity: isLoaded ? 1 : 0,
    transition: "opacity 0.3s ease-in-out",
    position: "relative"
}));

const Header = (props) => {

    const { intl, language, calledservicesettings, openCreateCalledDialog, logoimagesrc, loading } = props
    const { title, showtitle, enablecreatecalled, enablelogin, showknowledgebases, knowledgebasestext, logoimage, defaultcolor, defaulttextcolor, enablefaq, clientpageoption, createcalledpagetext } = props.clientportal

    let history = useHistory()

    const [isLoaded, setIsLoaded] = useState(false);

    const getLanguage = () => {
        let lang = window.location.pathname.split('/')[1]
        if (lang !== "pt" && lang !== "es" && lang !== "en") lang = "pt"
        return lang
    }

    const gotToLogin = () => history.push(`/${getLanguage()}`)
    const gotToKnowledgeBases = () => history.push(`/${getLanguage()}/knowledgebases-external`)
    const gotToClientPortal = () => history.push(`/${getLanguage()}/client-portal-external`)

    const handleLoad = useCallback(() => {
        setIsLoaded(true);
    }, []);

    const handleCreateCalled = () => {
        let frontCalledServiceUrl = Api.frontCalledServiceUrl()
        if (calledservicesettings.length == 1) {
            let route = `${frontCalledServiceUrl}${language}/${calledservicesettings[0].calledservicesettings.route}`
            window.open(`${route}`, '_blank')
        } else {
            const calledServiceSettingsList = calledservicesettings.map(item => ({
                id: item.calledservicesettings.id,
                title: item.calledservicesettings.title,
                OrganizationText: item.calledservicesettings.organization?.name || '',
                RouteText: `${frontCalledServiceUrl}${language}/${item.calledservicesettings.route}`
            }))
            openCreateCalledDialog(calledServiceSettingsList)
        }
    }

    const linkColor = defaulttextcolor ? defaulttextcolor : 'primary'

    const verticalDivider = () => <Divider orientation='vertical' flexItem sx={{ height: 24, alignSelf: 'center', mx: 0.5 }} />

    if (loading) return <HeaderExternalSkeleton />

    return (
        <Grid item xs={12} width="100%">
            <Toolbar sx={{ px: 0, height: 82, minHeight: 82 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    px: { xs: 2, md: '20vw' },
                    flexWrap: 'nowrap',
                    gap: 1,
                    py: 2,
                }}>
                    {/* Logo + título */}
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', flexShrink: 1, minWidth: 0, overflow: 'hidden' }}
                        onClick={gotToClientPortal}
                    >
                        {logoimagesrc && logoimagesrc.length > 0 && (
                            <>
                                {!isLoaded && (
                                    <Skeleton
                                        variant="rectangular"
                                        width={140}
                                        height={44}
                                        sx={{ borderRadius: 1 }}
                                    />
                                )}
                                <LogoImage
                                    src={logoimagesrc}
                                    alt={logoimage ? logoimage.description : "logomarca"}
                                    loading="lazy"
                                    decoding="async"
                                    isLoaded={isLoaded}
                                    onLoad={handleLoad}
                                />
                            </>
                        )}
                        {title && showtitle && (
                            <>
                                {verticalDivider()}
                                <Typography variant='h5' color={linkColor} noWrap sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                                    {title}
                                </Typography>
                            </>
                        )}
                    </Box>

                    {/* Nav links + botão */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexWrap: 'nowrap' }}>
                        {enablelogin && (
                            <>
                                <Typography
                                    variant='h5'
                                    sx={{ cursor: 'pointer', color: linkColor, fontSize: { xs: '0.8rem', sm: '1.25rem' }, whiteSpace: 'nowrap' }}
                                    onClick={gotToLogin}
                                >
                                    {intl.formatMessage({ id: "enter" })}
                                </Typography>
                                {(showknowledgebases || enablefaq || enablecreatecalled) && (
                                    verticalDivider()
                                )}
                            </>
                        )}

                        {showknowledgebases && (
                            <>
                                <Typography
                                    variant='h5'
                                    sx={{ cursor: 'pointer', color: linkColor, fontSize: { xs: '0.8rem', sm: '1.25rem' }, whiteSpace: 'nowrap', textAlign: 'center' }}
                                    onClick={gotToKnowledgeBases}
                                >
                                    {knowledgebasestext || ''}
                                </Typography>
                                {(enablefaq || enablecreatecalled) && (
                                    verticalDivider()
                                )}
                            </>
                        )}

                        {enablefaq && (
                            <>
                                <Typography
                                    variant='h5'
                                    sx={{ cursor: 'pointer', color: linkColor, fontSize: { xs: '0.8rem', sm: '1.25rem' }, whiteSpace: 'nowrap' }}
                                    onClick={() => history.push(`/${getLanguage()}/faq-external-client`)}
                                >
                                    {intl.formatMessage({ id: "faq" })}
                                </Typography>
                                {enablecreatecalled && (
                                    verticalDivider()
                                )}
                            </>
                        )}

                        {enablecreatecalled && (
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleCreateCalled}
                                sx={{
                                    backgroundColor: defaultcolor && defaultcolor.length > 0 ? `${defaultcolor} !important` : undefined,
                                    color: defaultcolor && defaultcolor.length > 0 ? '#fff' : undefined,
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    px: { xs: 1, sm: 2 },
                                    '&:hover': defaultcolor && defaultcolor.length > 0 ? {
                                        backgroundColor: `${darken(defaultcolor, 0.2)} !important`,
                                    } : {},
                                }}
                                color={defaultcolor && defaultcolor.length > 0 ? 'primary' : 'success'}
                            >
                                {createcalledpagetext || intl.formatMessage({ id: "send.request" })}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Toolbar>
        </Grid>
    )
}

export default Header;
